import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';

import type { BaseProps, ForwardRefForwardPropsComponent, NoChildrenProp } from '../../types';
import Link from '../Link';
import NoValue from '../NoValue/NoValue';

import FileItem from './FileItem';
import type { FileItemProps } from './FileItem';

export interface FileDisplayProps
  extends Pick<
      FileItemProps,
      'id' | 'onPreview' | 'onPreviewPopoverClick' | 'onDownload' | 'onOpen' | 'previewFromActions'
    >,
    BaseProps,
    NoChildrenProp {
  /** URL address */
  value?: string;
  /** The text to be displayed. */
  displayText?: string;
  /**
   * Display value either as file, link only or textual representation.
   * @default 'file'
   */
  variant?: 'file' | 'link' | 'text';
  /**
   * Used for a compact version of variant 'file' type
   * @default false
   */
  compact?: FileItemProps['compact'];
  /** Thumbnail image. */
  thumbnail?: string;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement | HTMLAnchorElement | HTMLSpanElement>;
}

const FileDisplay: ForwardRefForwardPropsComponent<FileDisplayProps> = forwardRef(
  function FileDisplay(
    {
      id,
      displayText,
      value,
      variant,
      thumbnail,
      onPreview,
      onPreviewPopoverClick,
      onDownload,
      onOpen,
      previewFromActions,
      compact = false,
      ...restProps
    }: PropsWithoutRef<FileDisplayProps>,
    ref: FileDisplayProps['ref']
  ) {
    if (variant === 'text' || !value) {
      return (
        <span ref={ref} {...restProps}>
          {value ? displayText || value : <NoValue />}
        </span>
      );
    }

    if (variant === 'link')
      return (
        <Link ref={ref as Ref<HTMLAnchorElement>} {...restProps} href={value}>
          {displayText || value}
        </Link>
      );

    return (
      <FileItem
        {...restProps}
        id={id}
        onOpen={onOpen}
        ref={ref as Ref<HTMLDivElement>}
        format={value.split('.').pop() ?? value}
        name={displayText || value}
        thumbnail={thumbnail}
        onDownload={onDownload}
        onPreview={onPreview}
        onPreviewPopoverClick={onPreviewPopoverClick}
        compact={compact}
        previewFromActions={previewFromActions}
      />
    );
  }
);

export default FileDisplay;
